import {
  Box,
  Button,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import {
  DataGridPro,
  GridFooterContainer,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import { eachDayOfInterval, format, parseISO } from "date-fns";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

import {
  addSearchTermClear,
  advanceBusinessReportSelector,
  createSearchTerm,
  updateSearchTermsRelevancy,
} from "../../../../../../redux/slices/advance_business_report";
import { storeAnalyticsSelector } from "../../../../../../redux/slices/store_analytics";
import { isNonEmptyObject } from "../../ProductSKUDashboard/HistoricalCharts/helpers";

import {
  buildDateKeys,
  compareRankCells,
  createRankLookup,
  createWeightedRankDateIndex,
  findWeightedRankDateIndex,
  getFirstRankForTerm,
  getRankForDateKeys,
  getUniqueSearchTerms,
  matchesKeywordFilter,
  parseKeywordFilter,
} from "./rankTrackingUtils";

const CompText = styled(Typography)`
  font-family: "Inter", sans-serif;
  border: none;
  align-items: center;
  margin-left: 10px;
  color: #171717;
  padding-right: 20px;
  display: inline;
  flex-wrap: wrap;
  .text {
    color: #000000;
    font-weight: bold;
  }
`;

const CompImageItem = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 8px;
  vertical-align: bottom;
`;

let sparklineGradientSeq = 0;

// Sparkline component for weighted rank trend visualization
// Shows a trend line segment that connects across cells with gradient fill
const WeightedRankSparkline = ({
  prevValue,
  currentValue,
  nextValue,
  seriesStats,
}) => {
  // Stable per instance: a random id on every render would churn the SVG defs.
  const gradientIdRef = useRef(null);
  if (gradientIdRef.current === null) {
    sparklineGradientSeq += 1;
    gradientIdRef.current = `weighted-rank-gradient-${sparklineGradientSeq}`;
  }
  const gradientId = gradientIdRef.current;

  // Use viewBox coordinates - SVG will scale to fill container via CSS
  const width = 100;
  const height = 52;
  const paddingTop = 8;
  const paddingBottom = 18;

  if (!seriesStats || seriesStats.validCount < 2) return null;

  const minVal = seriesStats.min;
  const maxVal = seriesStats.max;
  const range = maxVal - minVal || 1;

  // Helper to calculate Y position (lower rank = higher position on graph)
  const getY = (val) => {
    if (!val || val <= 0) return height / 2;
    return (
      paddingTop +
      ((val - minVal) / range) * (height - paddingTop - paddingBottom)
    );
  };

  const prevY = prevValue ? getY(prevValue) : null;
  const currentY = getY(currentValue);
  const nextY = nextValue ? getY(nextValue) : null;

  // Calculate color based on position in range (gradient from green to red)
  const normalizedPosition = (currentValue - minVal) / range;

  const getGradientColor = (t) => {
    const clampedT = Math.max(0, Math.min(1, t));

    if (clampedT <= 0.5) {
      const localT = clampedT * 2;
      const r = Math.round(110 + (253 - 110) * localT);
      const g = Math.round(231 + (224 - 231) * localT);
      const b = Math.round(183 + (71 - 183) * localT);
      return `rgb(${r}, ${g}, ${b})`;
    } else {
      const localT = (clampedT - 0.5) * 2;
      const r = Math.round(253 + (252 - 253) * localT);
      const g = Math.round(224 + (165 - 224) * localT);
      const b = Math.round(71 + (165 - 71) * localT);
      return `rgb(${r}, ${g}, ${b})`;
    }
  };

  const lineColor = getGradientColor(normalizedPosition);

  // Build path segments using cubic bezier for smoother curves
  let linePath = "";
  let areaPath = "";

  const entryY = prevY !== null ? (prevY + currentY) / 2 : currentY;
  const exitY = nextY !== null ? (currentY + nextY) / 2 : currentY;

  if (prevY !== null && nextY !== null) {
    linePath = `M 0 ${entryY} C ${width * 0.3} ${currentY}, ${
      width * 0.7
    } ${currentY}, ${width} ${exitY}`;
  } else if (prevY !== null) {
    linePath = `M 0 ${entryY} Q ${
      width * 0.5
    } ${currentY}, ${width} ${currentY}`;
  } else if (nextY !== null) {
    linePath = `M 0 ${currentY} Q ${
      width * 0.5
    } ${currentY}, ${width} ${exitY}`;
  } else {
    linePath = `M 0 ${currentY} L ${width} ${currentY}`;
  }

  areaPath = linePath + ` L ${width} ${height} L 0 ${height} Z`;

  return (
    <svg
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
      }}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={lineColor} stopOpacity="0.3" />
          <stop offset="100%" stopColor={lineColor} stopOpacity="0.05" />
        </linearGradient>
      </defs>

      <path d={areaPath} fill={`url(#${gradientId})`} />

      <path
        d={linePath}
        fill="none"
        stroke={lineColor}
        strokeWidth={4}
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity={0.2}
      />

      <path
        d={linePath}
        fill="none"
        stroke={lineColor}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const DataTable = styled(DataGridPro)({
  ".MuiDataGrid-main>div:nth-child(3)": {
    display: "none",
  },
  "& .MuiDataGrid-cell": {
    padding: "0 !important",
  },
  "& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer": {
    display: "none",
  },
  // Sort buttons are hidden above to keep the headers compact; the sorted
  // column still shows its arrow so the active sort and direction are visible.
  "& .MuiDataGrid-columnHeader--sorted .MuiDataGrid-iconButtonContainer": {
    display: "flex",
  },
  "& .MuiDataGrid-columnHeadersInner .MuiDataGrid-columnHeaderDraggableContainer":
    {
      position: "absolute",
      left: "-5px",
    },
  "& .MuiDataGrid-pinnedColumns": {
    backgroundColor: "white",
  },
  "& .noFocusBorder:focus": {
    outline: "none",
  },
  "& .noFocusBorder:focus-within": {
    outline: "none",
  },
  // Weighted rank row - remove all gaps between cells for continuous sparkline
  "& .weighted-rank-cell": {
    padding: "0 !important",
    border: "none !important",
    overflow: "visible !important",
  },
  '& .MuiDataGrid-columnHeader[data-field="relevancy"] .MuiDataGrid-columnHeaderTitleContainer':
    {
      justifyContent: "center",
      marginTop: "15px",
      lineHeight: "0px",
    },
  "& .MuiDataGrid-columnHeaders, .MuiDataGrid-pinnedColumnHeaders": {
    minHeight: "70px!important",
  },
});

const customSortComparator = (a, b) => {
  const aValue = Number(a.toString().replace("-", ""));
  const bValue = Number(b.toString().replace("-", ""));
  return aValue - bValue;
};

// The grid multiplies a comparator's result by -1 for a descending sort, so the
// day columns read the active direction off the api to keep unranked cells last.
const getActiveSortDirection = (cellParams) =>
  cellParams?.api
    ?.getSortModel?.()
    ?.find((item) => item.field === cellParams.field)?.sort || "asc";

const rankCellSortComparator = (a, b, cellParams) =>
  compareRankCells(a, b, getActiveSortDirection(cellParams));

const getBackgroundColor = (position) => {
  if (position >= 1 && position < 6) return "rgba(76, 175, 80, 0.5)"; // Strong Green
  if (position >= 6 && position < 11) return "rgba(76, 175, 80, 0.2)"; // Light Green
  if (position >= 11 && position < 21) return "rgba(255, 235, 59, 0.2)"; // Yellow
  if (position >= 21 && position < 30) return "rgba(255, 152, 0, 0.2)"; // Orange
  if (position >= 30) return "rgba(244, 67, 54, 0.2)"; // Red
  return "transparent";
};

const CustomFooterContainer = styled(GridFooterContainer)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  padding: theme.spacing(2),
  height: "auto",
}));

const AddKeywordFooter = ({ marketPlace }) => {
  const dispatch = useDispatch();
  const { store } = useSelector(storeAnalyticsSelector);
  const { editSearchTermsLoading, addNewKeywordResponse } = useSelector(
    advanceBusinessReportSelector
  );
  const [newKeywordValue, setNewKeywordValue] = useState("");
  const [emptyKeywordError, setEmptyKeywordError] = useState(false);

  const handleNewKeywordChange = (e) => {
    setNewKeywordValue(e.target.value);
    if (emptyKeywordError) {
      setEmptyKeywordError(false);
    }
    if (isNonEmptyObject(addNewKeywordResponse)) {
      dispatch(addSearchTermClear());
    }
  };

  const handleAddNewKeyword = () => {
    if (newKeywordValue.trim().length === 0) {
      setEmptyKeywordError(true);
      return;
    }
    dispatch(
      createSearchTerm({
        store: store,
        marketPlace: marketPlace,
        search_terms: [newKeywordValue],
      })
    );
  };

  return (
    <CustomFooterContainer>
      <div style={{ marginLeft: "5px" }}>
        <TextField
          value={newKeywordValue}
          size="small"
          onChange={handleNewKeywordChange}
          placeholder="Add New Keyword"
          error={!!emptyKeywordError}
          // disabled={marketPlace !== "us"}
        />
        <Button
          variant="outlined"
          style={{
            minWidth: "0px",
            borderRadius: "25px",
            margin: "0px 4px 0px 4px",
            height: "35px",
            width: "35px",
          }}
          onClick={handleAddNewKeyword}
          disabled={editSearchTermsLoading}
        >
          +
        </Button>
        {addNewKeywordResponse && (
          <span
            style={{ color: addNewKeywordResponse.success ? "green" : "red" }}
          >
            {addNewKeywordResponse.success
              ? "Keyword added successfully, check back in a few hours"
              : addNewKeywordResponse.message}
          </span>
        )}
      </div>
    </CustomFooterContainer>
  );
};

const RankTracking = ({
  type,
  data,
  sponsoredData,
  ca_data,
  caSponsoredData,
  currentPeriodicity,
  marketPlace,
  height = 400,
  isModal = false,
  isLeaf = false,
  weightedRankData,
  keywordFilter = "",
}) => {
  const dispatch = useDispatch();
  const { editSearchTermsLoading } = useSelector(advanceBusinessReportSelector);
  const [competitorAsin, setCompetitorAsin] = useState("");

  const competitorRanksByAsin = useMemo(
    () =>
      marketPlace === "us"
        ? data?.ranks?.competitors_ranks?.ranks || {}
        : ca_data?.ranks?.competitors_ranks?.ranks || {},
    [marketPlace, data, ca_data]
  );
  const competitorsDetails = useMemo(
    () =>
      marketPlace === "us"
        ? data?.ranks?.competitors_ranks?.competitors_asin || []
        : ca_data?.ranks?.competitors_ranks?.competitors_asin || [],
    [marketPlace, data, ca_data]
  );
  const competitorOptions = useMemo(() => {
    const optionByAsin = new Map();

    competitorsDetails.forEach((comp) => {
      if (!comp?.asin) return;
      optionByAsin.set(comp.asin, {
        asin: comp.asin,
        monthlySold: Number(comp.monthly_sold),
        imageId: comp.images_csv?.split(",")?.[0] || "",
      });
    });

    Object.keys(competitorRanksByAsin || {}).forEach((asin) => {
      if (!optionByAsin.has(asin)) {
        optionByAsin.set(asin, {
          asin,
          monthlySold: Number.NaN,
          imageId: "",
        });
      }
    });

    return Array.from(optionByAsin.values()).sort((a, b) => {
      const monthlySoldA = Number.isFinite(a.monthlySold) ? a.monthlySold : -1;
      const monthlySoldB = Number.isFinite(b.monthlySold) ? b.monthlySold : -1;
      if (monthlySoldA !== monthlySoldB) {
        return monthlySoldB - monthlySoldA;
      }
      return a.asin.localeCompare(b.asin);
    });
  }, [competitorsDetails, competitorRanksByAsin]);
  const competitorAsinOptions = useMemo(
    () => competitorOptions.map((option) => option.asin),
    [competitorOptions]
  );

  useEffect(() => {
    if (type !== "competitors" || marketPlace !== "us") {
      return;
    }
    if (competitorAsinOptions.length === 0) {
      if (competitorAsin !== "") {
        setCompetitorAsin("");
      }
      return;
    }
    if (!competitorAsin || !competitorAsinOptions.includes(competitorAsin)) {
      setCompetitorAsin(competitorAsinOptions[0]);
    }
  }, [type, marketPlace, competitorAsinOptions, competitorAsin]);

  const ranks = useMemo(() => {
    return type === "competitors"
      ? marketPlace === "us"
        ? competitorRanksByAsin?.[competitorAsin] || []
        : []
      : marketPlace === "us"
      ? data?.ranks?.ranks || []
      : ca_data?.ranks?.ranks;
  }, [data, type, marketPlace, ca_data, competitorAsin, competitorRanksByAsin]);

  const sponsoredRanks = useMemo(() => {
    return type === "competitors"
      ? marketPlace === "us"
        ? sponsoredData.ranks?.competitors_ranks?.ranks[competitorAsin] || []
        : []
      : marketPlace === "us"
      ? sponsoredData?.ranks?.ranks || []
      : caSponsoredData?.ranks?.ranks;
  }, [sponsoredData, type, marketPlace, caSponsoredData, competitorAsin]);

  const organicRankLookup = useMemo(() => createRankLookup(ranks), [ranks]);
  const sponsoredRankLookup = useMemo(
    () => createRankLookup(sponsoredRanks),
    [sponsoredRanks]
  );

  const uniqueSearchTerms = useMemo(() => {
    const searchTerms = getUniqueSearchTerms(ranks, sponsoredRanks).map(
      (searchTerm) => {
        const organic = getFirstRankForTerm(organicRankLookup, searchTerm);
        const sponsored = getFirstRankForTerm(sponsoredRankLookup, searchTerm);
        return [
          searchTerm,
          organic?.search_volume_1w || sponsored?.search_volume_1w || "-",
          organic?.relevancy || sponsored?.relevancy || "-",
        ];
      }
    );

    searchTerms.sort((a, b) => b[1] - a[1]);

    return searchTerms;
  }, [ranks, sponsoredRanks, organicRankLookup, sponsoredRankLookup]);

  const apiRef = useGridApiRef();

  // if data is empty and ca_data is not empty, fall back to ca_data
  const resolvedData = useMemo(
    () =>
      Object.keys(data || {}).length === 0 &&
      Object.keys(ca_data || {}).length > 0
        ? ca_data
        : data,
    [data, ca_data]
  );

  const allDates = useMemo(() => {
    const startDate = resolvedData
      ? parseISO(resolvedData.start_date)
      : new Date();
    if (!resolvedData) {
      startDate.setDate(startDate.getDate() - 30);
    }
    const endDate = resolvedData ? parseISO(resolvedData.end_date) : new Date();

    if (currentPeriodicity === "day") {
      return eachDayOfInterval({ start: startDate, end: endDate });
    }
    if (currentPeriodicity === "week") {
      return Array.from(
        { length: moment(endDate).diff(moment(startDate), "weeks") + 1 },
        (_, i) => [
          moment(startDate).add(i, "week"),
          moment(startDate)
            .add(i + 1, "week")
            .subtract(1, "day"),
        ]
      );
    }
    if (currentPeriodicity === "month") {
      return Array.from(
        { length: moment(endDate).diff(moment(startDate), "months") + 1 },
        (_, i) => [
          moment(startDate).add(i, "month"),
          moment(startDate).add(i + 1, "month"),
        ]
      );
    }
    return resolvedData?.allDates || [];
  }, [resolvedData, currentPeriodicity]);

  const dateKeys = useMemo(
    () => buildDateKeys(allDates, currentPeriodicity),
    [allDates, currentPeriodicity]
  );

  const [isEditingRelevancies, setIsEditingRelevancies] = useState(false);
  const [updatedRelevancies, setUpdatedRelevancies] = useState({});
  const [relevancyError, setRelevancyError] = useState("");
  // Optimistic relevancies applied on top of the derived rows until the next refetch.
  const [relevancyOverrides, setRelevancyOverrides] = useState({});
  const { store } = useSelector(storeAnalyticsSelector);
  const canEditRelevancies = Boolean(
    resolvedData?.child_asin || (resolvedData?.parent_asin && isLeaf)
  );

  const computedRows = useMemo(() => {
    try {
      const updatedRows = uniqueSearchTerms.map((searchTerm, rowIndex) => {
        const row = {
          id: rowIndex,
          keywords: searchTerm[0],
          sv1w: searchTerm[1],
          relevancy: searchTerm[2],
        };

        dateKeys.forEach((columnDateKeys, colIndex) => {
          const matchingRank = getRankForDateKeys(
            organicRankLookup,
            searchTerm[0],
            columnDateKeys
          );
          const matchingSponsoredRank = getRankForDateKeys(
            sponsoredRankLookup,
            searchTerm[0],
            columnDateKeys
          );

          row[`date_${colIndex}`] = {
            position: matchingRank?.position || "-",
            tooltip: matchingRank?.tooltip || [],
            positionSponsored: matchingSponsoredRank?.position || null,
          };
        });
        return row;
      });

      return updatedRows;
    } catch (error) {
      console.error("Error updating rows:", error);
      return [];
    }
  }, [uniqueSearchTerms, dateKeys, organicRankLookup, sponsoredRankLookup]);

  // The weighted rank summary row is pinned above the keyword rows, so sorting
  // or filtering them never moves or hides it.
  const weightedRankRow = useMemo(() => {
    if (!weightedRankData || marketPlace === "ca") return null;
    try {
      const summaryRow = {
        id: "weighted-rank",
        keywords: "⚖️ Weighted Rank",
        sv1w: 999999999, // Rendered as "-"; the row is pinned, so it never sorts
        relevancy: "-",
        isWeightedRank: true,
      };

      const isWeightedRankAvailable = Boolean(
        currentPeriodicity === "day" &&
          weightedRankData?.dates &&
          weightedRankData?.weightedRank
      );
      const weightedDateIndex = isWeightedRankAvailable
        ? createWeightedRankDateIndex(weightedRankData.dates)
        : null;

      // Each sparkline needs the whole series, so resolve it once here instead
      // of re-deriving it from the row inside every renderCell.
      const weightedSeries = [];

      allDates.forEach((date, colIndex) => {
        if (!isWeightedRankAvailable) {
          weightedSeries.push(0);
          summaryRow[`date_${colIndex}`] = {
            position: "-",
            tooltip: [],
            positionSponsored: null,
            isWeightedRank: true,
          };
          return;
        }

        const dateIndex = findWeightedRankDateIndex(weightedDateIndex, date);
        const weightedValue =
          dateIndex !== -1 ? weightedRankData.weightedRank[dateIndex] : null;

        // Get breakdown data for this date if available
        // New structure: weightedRankBreakdown[dateIndex] = { weights_breakdown_ranked, weights_breakdown_unranked }
        // Old structure (fallback): weightedRankBreakdown[dateIndex] = { keyword: value, ... }
        const breakdownEntry =
          dateIndex !== -1 && weightedRankData?.weightedRankBreakdown
            ? weightedRankData.weightedRankBreakdown[dateIndex]
            : null;
        // Try new structure first, fallback to old structure
        const breakdownData =
          breakdownEntry?.weights_breakdown_ranked || breakdownEntry || null;
        // Get unranked breakdown if available (new structure only)
        const breakdownUnranked =
          breakdownEntry?.weights_breakdown_unranked || null;

        const position =
          weightedValue !== null && weightedValue !== undefined
            ? weightedValue.toFixed(1)
            : "-";

        weightedSeries.push(parseFloat(position) || 0);
        summaryRow[`date_${colIndex}`] = {
          position,
          tooltip: [],
          positionSponsored: null,
          isWeightedRank: true,
          breakdown: breakdownData,
          breakdownUnranked: breakdownUnranked,
          series: weightedSeries,
          seriesIndex: colIndex,
        };
      });

      if (isWeightedRankAvailable) {
        const seriesStats = weightedSeries.reduce(
          (stats, value) => {
            if (value && value > 0) {
              stats.validCount += 1;
              stats.min = Math.min(stats.min, value);
              stats.max = Math.max(stats.max, value);
            }
            return stats;
          },
          { min: Infinity, max: -Infinity, validCount: 0 }
        );

        allDates.forEach((date, colIndex) => {
          summaryRow[`date_${colIndex}`].seriesStats = seriesStats;
        });
      }

      return summaryRow;
    } catch (error) {
      console.error("Error building the weighted rank row:", error);
      return null;
    }
  }, [allDates, currentPeriodicity, weightedRankData, marketPlace]);

  const pinnedRows = useMemo(
    () => (weightedRankRow ? { top: [weightedRankRow] } : undefined),
    [weightedRankRow]
  );

  // Fresh data supersedes any optimistic relevancy edit.
  useEffect(() => {
    setRelevancyOverrides((previous) =>
      Object.keys(previous).length === 0 ? previous : {}
    );
  }, [computedRows]);

  const rows = useMemo(() => {
    if (Object.keys(relevancyOverrides).length === 0) return computedRows;

    return computedRows.map((row) =>
      relevancyOverrides[row.id] !== undefined
        ? { ...row, relevancy: relevancyOverrides[row.id] }
        : row
    );
  }, [computedRows, relevancyOverrides]);

  const keywordFilterSet = useMemo(
    () => parseKeywordFilter(keywordFilter),
    [keywordFilter]
  );

  // Only the grid sees the filtered rows; relevancy edits and validation keep
  // working against the full keyword list.
  const visibleRows = useMemo(
    () =>
      keywordFilterSet.size === 0
        ? rows
        : rows.filter((row) =>
            matchesKeywordFilter(row.keywords, keywordFilterSet)
          ),
    [rows, keywordFilterSet]
  );

  const validateRelevancies = useCallback((originalRows, modifiedRows) => {
    const relevancyTracker = {};

    const allRows = originalRows.map((row) => {
      const modified = modifiedRows.find(
        (modifiedRow) => modifiedRow.term === row.keywords
      );
      return {
        ...row,
        relevancy: modified ? modified.relevancy : row.relevancy,
      };
    });

    const isValid = allRows.reduce((valid, row) => {
      if (!valid) return false;

      const relevancy = row.relevancy;

      if (typeof relevancy === "number" && !isNaN(relevancy)) {
        if (relevancy !== 1 && relevancy !== 2 && relevancy !== 3) {
          setRelevancyError("Invalid values");
          return false;
        }
        if (relevancyTracker[relevancy]) {
          setRelevancyError("Duplicate values");
          return false;
        }
        relevancyTracker[relevancy] = true;
      }

      return true;
    }, true);

    return isValid;
  }, []);

  const handleRelevancySave = useCallback(
    (asin) => {
      if (!asin) {
        setRelevancyError(
          "Relevancy can only be edited at child ASIN level. Open the modal from a child row."
        );
        setIsEditingRelevancies(false);
        return;
      }
      const modifiedRows = rows.reduce((result, row) => {
        const editedValue = updatedRelevancies[row.id];
        let currentRelevancy =
          editedValue !== undefined ? editedValue : row.relevancy;

        if (!isNaN(currentRelevancy)) {
          currentRelevancy = Number(currentRelevancy);
        }

        if (currentRelevancy !== row.relevancy) {
          result.push({
            child_asin: asin,
            term: row.keywords,
            relevancy: currentRelevancy,
          });
        }
        return result;
      }, []);

      if (modifiedRows.length === 0) {
        setIsEditingRelevancies(false);
        return;
      }

      if (!validateRelevancies(rows, modifiedRows)) {
        return;
      }

      setRelevancyOverrides(
        Object.entries(updatedRelevancies).reduce(
          (overrides, [rowId, relevancy]) => {
            overrides[rowId] = !isNaN(Number(relevancy))
              ? Number(relevancy)
              : relevancy;
            return overrides;
          },
          {}
        )
      );

      dispatch(
        updateSearchTermsRelevancy({
          store: store,
          child_asin: asin,
          rows: modifiedRows.filter((row) => !isNaN(Number(row.relevancy))),
        })
      );
      setIsEditingRelevancies(false);
    },
    [rows, updatedRelevancies, validateRelevancies, dispatch, store]
  );

  const handleRelevancyCancel = useCallback(() => {
    setUpdatedRelevancies({});
    setRelevancyError("");
    setIsEditingRelevancies(false);
  }, []);

  const columns = useMemo(
    () => [
      {
        field: "keywords",
        headerName:
          keywordFilterSet.size === 0
            ? `Keywords (${uniqueSearchTerms.length})`
            : `Keywords (${visibleRows.length}/${uniqueSearchTerms.length})`,
        minWidth: 250,
        renderCell: (params) => {
          const value = params.value;
          const isWeightedRankRow = params.row.isWeightedRank;
          return (
            <div
              style={{
                padding: "5px 10px",
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                fontWeight: "600",
                backgroundColor: isWeightedRankRow
                  ? "rgba(248, 250, 252, 0.9)"
                  : "transparent",
                borderBottom: isWeightedRankRow
                  ? "1px solid rgba(148, 163, 184, 0.15)"
                  : "none",
              }}
            >
              {value}
            </div>
          );
        },
      },
      {
        field: "sv1w",
        headerName: "SV (1w)",
        minWidth: 50,
        sortComparator: customSortComparator,
        renderCell: (params) => {
          const isWeightedRankRow = params.row.isWeightedRank;
          return (
            <div
              style={{
                padding: "0px 10px",
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                backgroundColor: isWeightedRankRow
                  ? "rgba(248, 250, 252, 0.9)"
                  : "transparent",
                borderBottom: isWeightedRankRow
                  ? "1px solid rgba(148, 163, 184, 0.15)"
                  : "none",
              }}
            >
              {isWeightedRankRow ? "-" : params.value}
            </div>
          );
        },
      },
      {
        field: "relevancy",
        headerName: "Relevancy",
        minWidth: 150,
        sortComparator: customSortComparator,
        renderHeader: () => (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <div style={{ lineHeight: "1", margin: "6px 0 0 0", padding: "0" }}>
              Relevancy
            </div>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "2px" }}
            >
              {!isEditingRelevancies ? (
                <>
                  <Tooltip
                    title={
                      canEditRelevancies
                        ? "Edit relevancies"
                        : "Open the modal from a child ASIN row to edit relevancies"
                    }
                  >
                    <span>
                      <Button
                        variant="text"
                        size="small"
                        style={{ padding: "0px", fontSize: "0.7rem" }}
                        onClick={(event) => {
                          event.stopPropagation();
                          if (canEditRelevancies) setIsEditingRelevancies(true);
                        }}
                        disabled={!canEditRelevancies}
                      >
                        Edit
                      </Button>
                    </span>
                  </Tooltip>
                </>
              ) : (
                <>
                  <div style={{ flexDirection: "row" }}>
                    <Button
                      variant="text"
                      size="small"
                      style={{
                        padding: "0px",
                        fontSize: "0.7rem",
                        textTransform: "none",
                      }}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleRelevancySave(resolvedData?.child_asin);
                      }}
                    >
                      Save
                    </Button>
                    <Button
                      variant="text"
                      size="small"
                      style={{
                        padding: "0px",
                        fontSize: "0.7rem",
                        textTransform: "none",
                        color: "red",
                      }}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleRelevancyCancel();
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                  {relevancyError && (
                    <span
                      style={{
                        color: "red",
                        height: "14px",
                        textAlign: "center",
                      }}
                    >
                      {" "}
                      {relevancyError}{" "}
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
        ),
        renderCell: (params) => {
          const handleRelevancyChange = (e) => {
            if (e.target.value === "") {
              e.target.value = "-";
            } else if (e.target.value.length > 1) {
              e.target.value = e.target.value.slice(1);
            }
            setUpdatedRelevancies((prev) => ({
              ...prev,
              [params.row.id]: e.target.value,
            }));
            if (relevancyError) {
              setRelevancyError("");
            }
          };

          const currentRelevancyValue =
            updatedRelevancies[params.row.id] ?? params.value;

          const isWeightedRankRow = params.row.isWeightedRank;
          return (
            <div
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: isWeightedRankRow
                  ? "rgba(248, 250, 252, 0.9)"
                  : "transparent",
                borderBottom: isWeightedRankRow
                  ? "1px solid rgba(148, 163, 184, 0.15)"
                  : "none",
              }}
            >
              {isEditingRelevancies || editSearchTermsLoading ? (
                <TextField
                  sx={{
                    width: "45px",
                  }}
                  size="small"
                  value={currentRelevancyValue}
                  onChange={handleRelevancyChange}
                  disabled={editSearchTermsLoading || isWeightedRankRow}
                />
              ) : (
                <span>{currentRelevancyValue}</span>
              )}
            </div>
          );
        },
        cellClassName: "noFocusBorder",
        headerClassName: "noFocusBorder",
      },
      ...allDates.map((date, index) => ({
        field: `date_${index}`,
        headerName:
          currentPeriodicity === "day"
            ? format(date, "d MMM")
            : currentPeriodicity === "week"
            ? `${date[0].format("DD MMM")} - ${date[1].format("DD MMM")}`
            : currentPeriodicity === "month"
            ? `${date[0].format("MMM")} ${date[0].format("YYYY")}`
            : "",
        type: "number",
        sortComparator: rankCellSortComparator,
        headerClassName: "position-header",
        width: currentPeriodicity === "day" ? 70 : 140,
        headerAlign: "center",
        renderCell: (params) => {
          const cellValue = params.value || {
            position: "-",
            tooltip: [],
            positionSponsored: null,
          };
          const value = cellValue.position;
          const valueSponsored = cellValue.positionSponsored;
          const isWeightedRankRow = cellValue.isWeightedRank;
          const backgroundColor = isWeightedRankRow
            ? "rgba(248, 250, 252, 0.9)"
            : getBackgroundColor(value);

          // Special rendering for weighted rank row with sparkline
          if (isWeightedRankRow) {
            const currentNumericValue = parseFloat(value) || 0;
            const breakdown = cellValue.breakdown;
            const breakdownUnranked = cellValue.breakdownUnranked;

            // Series and stats are precomputed when the rows are built.
            const series = cellValue.series || [];
            const seriesStats = cellValue.seriesStats;
            const currentIdx = cellValue.seriesIndex ?? -1;

            // For modal (reversed columns), prev/next are visually swapped
            // Visual prev = data next, Visual next = data prev
            const prevIdx = isModal ? currentIdx + 1 : currentIdx - 1;
            const nextIdx = isModal ? currentIdx - 1 : currentIdx + 1;
            const prevValue =
              prevIdx >= 0 && prevIdx < series.length ? series[prevIdx] : null;
            const nextValue =
              nextIdx >= 0 && nextIdx < series.length ? series[nextIdx] : null;

            // Format breakdown tooltip content
            const formatBreakdownTooltip = (breakdownData) => {
              if (!breakdownData || typeof breakdownData !== "object") {
                return null;
              }
              return Object.entries(breakdownData)
                .sort(([, a], [, b]) => b - a) // Sort by value descending
                .map(([keyword, contribution]) => (
                  <Box
                    key={keyword}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 2,
                      fontSize: "12px",
                    }}
                  >
                    <Typography sx={{ fontSize: "12px", color: "#e2e8f0" }}>
                      {keyword}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#ffffff",
                      }}
                    >
                      {typeof contribution === "number"
                        ? contribution.toFixed(2)
                        : contribution}
                    </Typography>
                  </Box>
                ));
            };

            const tooltipContent = formatBreakdownTooltip(breakdown);
            const tooltipContentUnranked =
              formatBreakdownTooltip(breakdownUnranked);
            const hasBreakdown = tooltipContent && tooltipContent.length > 0;
            const hasUnranked =
              tooltipContentUnranked && tooltipContentUnranked.length > 0;

            const cellContent = (
              <div
                style={{
                  padding: 0,
                  margin: 0,
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                  overflow: "visible",
                }}
              >
                {seriesStats?.validCount > 1 && (
                  <WeightedRankSparkline
                    prevValue={prevValue}
                    currentValue={currentNumericValue}
                    nextValue={nextValue}
                    seriesStats={seriesStats}
                  />
                )}
                <span
                  style={{
                    position: "relative",
                    zIndex: 1,
                    fontWeight: 600,
                    fontSize: "13px",
                    color: "#1e293b",
                    textShadow: "0 1px 2px rgba(255,255,255,0.8)",
                  }}
                >
                  {value === 0 || value === "0" || value === "0.0"
                    ? "-"
                    : value}
                </span>
              </div>
            );

            return hasBreakdown || hasUnranked ? (
              <Tooltip
                title={
                  <Box
                    sx={{
                      p: 1,
                      maxHeight: 400,
                      overflow: "auto",
                    }}
                  >
                    {hasBreakdown && (
                      <>
                        <Typography
                          sx={{
                            fontSize: "13px",
                            fontWeight: 600,
                            mb: 1,
                            color: "#f8fafc",
                          }}
                        >
                          Weighted Rank Breakdown
                        </Typography>
                        {tooltipContent}
                      </>
                    )}
                    {hasUnranked && (
                      <>
                        <Typography
                          sx={{
                            fontSize: "13px",
                            fontWeight: 600,
                            mt: hasBreakdown ? 2 : 0,
                            mb: 1,
                            color: "#fbbf24",
                          }}
                        >
                          Important terms not taken into account (Unranked or
                          Untracked)
                        </Typography>
                        {tooltipContentUnranked}
                      </>
                    )}
                  </Box>
                }
                arrow
                placement="top"
              >
                {cellContent}
              </Tooltip>
            ) : (
              cellContent
            );
          }

          return (
            <Tooltip
              disableHoverListener={
                currentPeriodicity !== "day" ||
                (cellValue.tooltip?.length ?? 0) === 0
              }
              title={
                <Box>
                  {[...(cellValue.tooltip || [])]
                    .sort((a, b) => {
                      const hourA = moment(a.last_updated_date).format("HH");
                      const hourB = moment(b.last_updated_date).format("HH");
                      return hourA - hourB;
                    })
                    .map((positions, index) => (
                      <Box key={index} sx={{ display: "flex" }}>
                        <Typography sx={{ width: 50 }}>
                          {moment(positions.last_updated_date).format("hh A")}
                        </Typography>
                        <Typography sx={{ width: 20 }}>-</Typography>
                        <Typography>{positions.overall_position}</Typography>
                      </Box>
                    ))}
                </Box>
              }
              arrow
            >
              <div
                style={{
                  padding: 5,
                  background: backgroundColor,
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "600",
                  borderLeft: "1px solid #e0e0e0",
                  position: "relative",
                  // background:
                  //   "linear-gradient(to right bottom, " +
                  //   "rgba(0, 0, 255, 0.2)" +
                  //   " 25%, " +
                  //   backgroundColor +
                  //   " 25.3%)",
                }}
              >
                {valueSponsored && (
                  <>
                    <div
                      style={{
                        display: "inline-block",
                        height: "0",
                        width: "0",
                        borderTop: "40px solid rgb(233 213 255)",
                        borderRight: "40px solid transparent",
                        position: "absolute",
                        top: "0",
                        left: "0",
                      }}
                    />
                    <span
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        height: "20px",
                        width: "20px",
                        textAlign: "center",
                      }}
                    >
                      {valueSponsored}
                    </span>
                  </>
                )}
                {value}
              </div>
            </Tooltip>
          );
        },
        cellClassName: (params) =>
          params.value?.isWeightedRank ? "weighted-rank-cell" : "",
      })),
    ],
    [
      allDates,
      canEditRelevancies,
      currentPeriodicity,
      editSearchTermsLoading,
      handleRelevancyCancel,
      handleRelevancySave,
      isEditingRelevancies,
      isModal,
      keywordFilterSet.size,
      relevancyError,
      resolvedData,
      uniqueSearchTerms.length,
      updatedRelevancies,
      visibleRows.length,
    ]
  );

  const gridColumns = useMemo(
    () => (isModal ? [...columns].reverse() : columns),
    [columns, isModal]
  );

  useEffect(() => {
    if (isModal) return;

    const timeoutId = setTimeout(() => {
      if (apiRef?.current?.scrollToIndexes && apiRef.current.getAllColumns) {
        apiRef.current.scrollToIndexes({
          colIndex: apiRef.current.getAllColumns().length - 1,
        });
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [isModal, apiRef, gridColumns, rows]);

  const selectedCompetitor = competitorsDetails.find(
    (comp) => comp.asin === competitorAsin
  );
  const competitorImageId = selectedCompetitor?.images_csv?.split(",")[0];
  const competitorPrice =
    selectedCompetitor?.stats?.current?.["Marketplace price"] ?? "-";
  const competitorMonthlySold = Number(selectedCompetitor?.monthly_sold);
  const competitorTitleTooltip =
    selectedCompetitor?.title || selectedCompetitor?.brand || "-";

  return (
    <div style={{ height: height, width: "100%" }}>
      {type === "competitors" && marketPlace === "us" && (
        <div>
          <Select
            value={competitorAsin}
            onChange={(e) => setCompetitorAsin(e.target.value)}
            disabled={competitorAsinOptions.length === 0}
          >
            {competitorOptions.map((option, index) => (
              <MenuItem key={index} value={option.asin}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {option.imageId ? (
                    <img
                      src={`https://images-na.ssl-images-amazon.com/images/I/${option.imageId}`}
                      alt={option.asin}
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: 4,
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: 1,
                        backgroundColor: "#e5e7eb",
                      }}
                    />
                  )}
                  <span>{option.asin}</span>
                </Box>
              </MenuItem>
            ))}
          </Select>
          <CompText>
            <CompImageItem
              src={
                competitorImageId
                  ? `https://images-na.ssl-images-amazon.com/images/I/${competitorImageId}`
                  : ""
              }
            />{" "}
            <Tooltip title={competitorTitleTooltip} arrow>
              <a
                href={`https://www.amazon.com/dp/${competitorAsin}?th=1`}
                target="_blank"
                rel="noreferrer"
              >
                <span>{selectedCompetitor?.brand || "-"}</span>
              </a>
            </Tooltip>
            {` at a current price of $`}
            <span className="text">{competitorPrice}</span>
            {` sold around `}
            <span className="text">
              {Number.isFinite(competitorMonthlySold)
                ? Intl.NumberFormat("en", { notation: "compact" }).format(
                    competitorMonthlySold
                  )
                : "-"}
            </span>
            {` units last month.`}
          </CompText>
        </div>
      )}
      <Box height={20} />
      <DataTable
        disableSelectionOnClick
        disableColumnMenu
        apiRef={apiRef}
        rows={visibleRows}
        pinnedRows={pinnedRows}
        columns={gridColumns}
        initialState={{
          pinnedColumns: { left: ["keywords", "sv1w", "relevancy"] },
          sorting: {
            sortModel: [{ field: "sv1w", sort: "desc" }],
          },
        }}
        rowsPerPageOptions={[5, 10, 15]}
        sx={{
          // 16px centres "7 Oct" and still leaves room for "30 Sep" in 70px.
          "& .position-header .MuiDataGrid-columnHeaderTitle": {
            marginLeft: 2,
          },
          // A sorted day header has to fit the date and its arrow in 70px, so
          // the margin above goes and the arrow button loses its padding.
          "& .position-header.MuiDataGrid-columnHeader--sorted .MuiDataGrid-columnHeaderTitle":
            {
              marginLeft: 0,
            },
          "& .position-header .MuiDataGrid-iconButtonContainer .MuiIconButton-root":
            {
              padding: 0,
            },
        }}
        slots={{ footer: AddKeywordFooter }}
        slotProps={{ footer: { marketPlace } }}
      />
    </div>
  );
};

export default RankTracking;
